import { useCallback, useEffect, useState } from "react";
import { formatAmount } from "../../include/money";
import IndiaDataGrid from "../../include/IndiaDataGrid";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, gridSx, primaryButtonSx } from "./consoleTheme";
import { indiaDate } from "../../include/indiaDate";

function statusLabel(status) {
  if (status === "2") return "Pending";
  if (status === "0") return "Disabled";
  return "Enabled";
}

const emptyForm = {
  store_name: "",
  owner_name: "",
  email: "",
  contact_mobile: "",
  username: "",
  password: "",
  area_name: "",
  business_type_id: "",
  state_id: "",
  city_id: "",
  store_image: "",
  cashi_plus_flag: "0",
  licence_days: "365",
  wallet_amount: "0",
  allow_loyalty_edit: "1",
};

function licenceLabel(row) {
  if (row.days_left < 0) return "Expired";
  if (row.days_left <= 15) return `${row.days_left} days left`;
  return "Active";
}

export default function StoreMaster() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [clubId, setClubId] = useState("");
  const [clubs, setClubs] = useState([]);

  const load = useCallback(async (searchKey = "", club = "") => {
    const result = await adminPost("ADMIN_STORE_LIST", { searchKey, club_id: club });
    if (result.status === "200" && Array.isArray(result.data)) setRows(result.data);
    else toast.error(result?.status_message || "Could not load stores");
  }, []);

  useEffect(() => { load("", ""); }, [load]);
  useEffect(() => {
    adminPost("CLUB_LIST").then((result) => {
      if (result.status === "200") setClubs(result.data?.clubs || []);
    });
  }, []);

  const updateRow = (store) => {
    setRows((prev) => prev.map((row) => (row.store_id === store.store_id ? store : row)));
  };

  const setStatus = async (row, status) => {
    const result = await adminPost("ADMIN_STORE_STATUS", { store_id: row.store_id, status });
    if (result.status === "200") {
      updateRow(result.data.store);
      toast.success(result.data.message);
    } else {
      toast.error(result?.status_message || "Update failed");
    }
  };

  const columns = [
    { field: "store_name", headerName: "Store name", flex: 1.3, minWidth: 160 },
    { field: "club_name", headerName: "Club", flex: 1, minWidth: 140, valueGetter: (value, row) => row.club_name || "—" },
    { field: "owner_name", headerName: "Owner name", flex: 1, minWidth: 140 },
    { field: "email", headerName: "Email", flex: 1.2, minWidth: 180 },
    { field: "business_type_name", headerName: "Business", flex: 1, minWidth: 130 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      valueGetter: (value, row) => statusLabel(row.status),
      renderCell: (params) => (
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: params.row.status === "1" ? "#15803d" : params.row.status === "2" ? "#b45309" : "#b91c1c" }}>
          {statusLabel(params.row.status)}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 280,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/stores/${params.row.store_id}`)}>View</Button>
          <Button size="small" sx={{ textTransform: "none" }} onClick={() => navigate(`/console/stores/${params.row.store_id}/edit`)}>Edit</Button>
          {params.row.status === "2" ? (
            <Button size="small" sx={{ textTransform: "none" }} onClick={() => setStatus(params.row, "1")}>Approve</Button>
          ) : (
            <Button size="small" sx={{ textTransform: "none" }} onClick={() => setStatus(params.row, params.row.status === "1" ? "0" : "1")}>
              {params.row.status === "1" ? "Disable" : "Enable"}
            </Button>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center", background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 1.5 }}>
        <TextField
          size="small"
          label="Search store, owner, mobile, or club"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(search, clubId); }}
          sx={{ minWidth: 240, flex: 1 }}
        />
        <TextField
          select
          size="small"
          label="Club"
          value={clubId}
          onChange={(e) => {
            const next = e.target.value;
            setClubId(next);
            load(search, next);
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All clubs</MenuItem>
          <MenuItem value="none">No club</MenuItem>
          {clubs.map((club) => (
            <MenuItem key={club.club_id} value={club.club_id}>{club.title || club.code}</MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={() => load(search, clubId)} sx={{ textTransform: "none", borderColor: consoleColors.line, color: consoleColors.ink }}>Search</Button>
        <Button variant="outlined" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/storeregister`); toast.success("Register link copied"); }} sx={{ textTransform: "none", borderColor: consoleColors.line, color: consoleColors.ink }}>Copy register link</Button>
        <Button variant="contained" sx={primaryButtonSx} onClick={() => navigate("/console/stores/new")}>Add store</Button>
      </Box>

      <Box sx={{ height: 640, background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, overflow: "hidden" }}>
        <IndiaDataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.store_id}
          rowHeight={52}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          sx={gridSx}
        />
      </Box>
    </Box>
  );
}

function DetailLine({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 12, color: consoleColors.muted }}>{label}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 650, color: consoleColors.ink }}>{value || "—"}</Typography>
    </Box>
  );
}

export function StoreDetail() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [days, setDays] = useState("");

  const load = useCallback(async () => {
    const result = await adminPost("ADMIN_STORE_DETAIL", { store_id: storeId });
    if (result.status === "200") {
      const next = result.data?.store || null;
      setStore(next);
      setDays(String(next?.licence_days ?? ""));
    } else toast.error(result?.status_message || "Could not load this store");
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    const result = await adminPost("ADMIN_STORE_STATUS", { store_id: storeId, status });
    if (result.status === "200") {
      toast.success(result.data.message);
      load();
    } else toast.error(result?.status_message || "Update failed");
  };

  const saveLicence = async () => {
    const result = await adminPost("ADMIN_STORE_LICENCE", { store_id: storeId, licence_days: Number(days) });
    if (result.status === "200") {
      toast.success(result.data.message);
      load();
    } else toast.error(result?.data?.error || result?.status_message || "Could not update licence");
  };

  if (!store) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button sx={{ textTransform: "none" }} onClick={() => navigate("/console/stores")}>Back to stores</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 920 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate("/console/stores")}>Back to stores</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
          {store.store_image && <Box component="img" src={store.store_image} alt="" sx={{ width: 84, height: 84, objectFit: "cover", borderRadius: 2 }} />}
          <Box sx={{ flex: 1, minWidth: 180 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800 }}>{store.store_name}</Typography>
            <Typography sx={{ fontWeight: 700, color: store.status === "1" ? "#15803d" : store.status === "2" ? "#b45309" : "#b91c1c" }}>{statusLabel(store.status)}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button sx={{ textTransform: "none" }} onClick={() => navigate(`/console/stores/${store.store_id}/edit`)}>Edit</Button>
            {store.status === "2" ? (
              <Button variant="contained" sx={primaryButtonSx} onClick={() => setStatus("1")}>Approve</Button>
            ) : (
              <Button variant="outlined" sx={{ textTransform: "none" }} onClick={() => setStatus(store.status === "1" ? "0" : "1")}>
                {store.status === "1" ? "Disable" : "Enable"}
              </Button>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.6 }}>
          <DetailLine label="Owner name" value={store.owner_name} />
          <DetailLine label="Club" value={store.club_name} />
          <DetailLine label="Business" value={store.business_type_name} />
          <DetailLine label="Username" value={store.username} />
          <DetailLine label="Email" value={store.email} />
          <DetailLine label="Mobile" value={store.contact_mobile} />
          <DetailLine label="Plan" value={store.plan_name} />
          <DetailLine label="Area" value={store.area_name} />
          <DetailLine label="City" value={store.city_name} />
          <DetailLine label="State" value={store.state_name} />
          <DetailLine label="Wallet" value={`₹${formatAmount(store.wallet_amount)}`} />
          <DetailLine label="Joining code" value={store.join_code} />
          <DetailLine label="Store id" value={store.store_id} />
          <DetailLine label="Valid till" value={indiaDate(store.licence_end)} />
          <DetailLine label="Licence" value={licenceLabel(store)} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2, flexWrap: "wrap" }}>
          <TextField size="small" label="Licence days" type="number" value={days} onChange={(event) => setDays(event.target.value)} sx={{ width: 160 }} />
          <Button variant="contained" sx={primaryButtonSx} onClick={saveLicence}>Save licence</Button>
        </Box>
      </Box>
    </Box>
  );
}

export function StoreForm() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("");
  const [options, setOptions] = useState({ types: [], states: [], cities: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const optionsResult = await adminPost("STORE_FORM_OPTIONS");
      if (!mounted) return;
      if (optionsResult.status === "200") {
        setOptions({ types: optionsResult.data?.types || [], states: optionsResult.data?.states || [], cities: optionsResult.data?.cities || [] });
      }
      if (!storeId) return;
      const detail = await adminPost("ADMIN_STORE_DETAIL", { store_id: storeId });
      if (!mounted || detail.status !== "200") return;
      const store = detail.data?.store || {};
      setStatus(String(store.status || ""));
      setForm({
        ...emptyForm,
        ...store,
        password: "",
        licence_days: String(store.licence_days ?? 365),
        wallet_amount: String(store.wallet_amount ?? 0),
      });
    })();
    return () => { mounted = false; };
  }, [storeId]);

  const citiesForState = options.cities.filter((city) => city.state_id === form.state_id);

  const onImage = (file) => {
    if (!file) return;
    if (file.size > 900000) {
      toast.error("Use a store image under 900 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((prev) => ({ ...prev, store_image: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const result = await adminPost(storeId ? "ADMIN_STORE_UPDATE" : "ADMIN_STORE_ADD", { ...form, store_id: storeId || "" });
      if (result.status === "200") {
        toast.success(result.data.message);
        navigate("/console/stores");
      } else {
        toast.error(result?.data?.error || result?.status_message || "Could not save the store");
      }
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    const result = await adminPost("ADMIN_STORE_STATUS", { store_id: storeId, status: "1" });
    if (result.status === "200") {
      setStatus("1");
      toast.success(result.data.message);
    } else {
      toast.error(result?.status_message || "Could not approve the store");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 760 }}>
      <Button sx={{ textTransform: "none", mb: 2 }} onClick={() => navigate("/console/stores")}>Back to stores</Button>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 750 }}>{storeId ? "Edit store" : "Add store"}</Typography>
        <TextField label="Store name" value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} />
        {storeId && <TextField label="Club" value={form.club_name || "—"} InputProps={{ readOnly: true }} />}
        <Button component="label" sx={{ textTransform: "none", justifyContent: "flex-start" }}>
          Store image {form.store_image ? "· selected" : ""}
          <input hidden type="file" accept="image/*" onChange={(e) => onImage(e.target.files?.[0])} />
        </Button>
        {form.store_image && <Box component="img" src={form.store_image} alt="Store" sx={{ width: 96, height: 96, objectFit: "cover", borderRadius: 1 }} />}
        <TextField select label="Business type" value={form.business_type_id} onChange={(e) => setForm({ ...form, business_type_id: e.target.value })}>
          <MenuItem value="" disabled>Select</MenuItem>
          {options.types.map((item) => <MenuItem key={item.type_id} value={item.type_id}>{item.name}</MenuItem>)}
        </TextField>
        <TextField label="Owner name" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
        <TextField required label="Email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} helperText="Required. The store signs in with a 6-digit code sent to this email." />
        <TextField label="Mobile" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} />
        <TextField label="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
        <TextField label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} helperText={storeId ? "Leave blank to keep the current password" : ""} />
        <TextField label="Area" value={form.area_name} onChange={(e) => setForm({ ...form, area_name: e.target.value })} />
        <TextField select label="State" value={form.state_id} onChange={(e) => setForm({ ...form, state_id: e.target.value, city_id: "" })}>
          <MenuItem value="" disabled>Select</MenuItem>
          {options.states.map((item) => <MenuItem key={item.state_id} value={item.state_id}>{item.name}</MenuItem>)}
        </TextField>
        <TextField select label="City" value={form.city_id} onChange={(e) => setForm({ ...form, city_id: e.target.value })} disabled={!form.state_id} helperText={form.state_id ? "Cities for the selected state" : "Choose a state first"}>
          <MenuItem value="" disabled>Select</MenuItem>
          {citiesForState.map((item) => <MenuItem key={item.city_id} value={item.city_id}>{item.name}</MenuItem>)}
        </TextField>
        <TextField select label="Plan" value={form.cashi_plus_flag} onChange={(e) => setForm({ ...form, cashi_plus_flag: e.target.value })}>
          <MenuItem value="0">Loyalty</MenuItem>
          <MenuItem value="1">Loyalty + Cashi Plus</MenuItem>
          <MenuItem value="2">Cashi Plus only</MenuItem>
          <MenuItem value="3">Lucky Draw Coupons</MenuItem>
        </TextField>
        <TextField label="Licence days" type="number" value={form.licence_days} onChange={(e) => setForm({ ...form, licence_days: e.target.value })} />
        <TextField select label="Store can change loyalty settings" value={form.allow_loyalty_edit || "1"} onChange={(e) => setForm({ ...form, allow_loyalty_edit: e.target.value })} helperText="If no, this store uses the console default, or the club settings when it is in a club.">
          <MenuItem value="1">Yes</MenuItem>
          <MenuItem value="0">No</MenuItem>
        </TextField>
        <TextField label={storeId ? "Wallet" : "Opening wallet"} type="number" value={form.wallet_amount} onChange={(e) => setForm({ ...form, wallet_amount: e.target.value })} />
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {storeId && status === "2" && <Button variant="outlined" sx={{ textTransform: "none" }} onClick={approve}>Approve</Button>}
          <Button onClick={() => navigate("/console/stores")} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" disabled={saving} onClick={save} sx={primaryButtonSx}>{saving ? "Saving..." : "Save store"}</Button>
        </Box>
      </Box>
    </Box>
  );
}
